import { action, atom } from '@reatom/framework'

import { Data } from '@blade/std-lib'

import type { SendSwapData } from '../atoms'
import type { CurrencyModel } from '@root/entities/currency'
import type { PairModel } from '@root/entities/pair'
import type { Quote } from '@root/entities/quote'
import type { ConfirmTransactionUpdatePayload } from '@root/entities/realtime'
import type { TradeSettings } from '@root/entities/trade-settings'
import type { TxHash } from '@root/entities/transaction'
import type { WalletModel } from '@root/entities/wallet'
import type { Error } from '@root/shared'
import type { ApiError } from '@root/shared/api'
import type BigNumber from 'bignumber.js'

class StageGettingQuote extends Data.TaggedClass('StageGettingQuote')<{}> {}

class StageSending extends Data.TaggedClass('StageSending')<{
  quote: Quote.Quote
}> {}

class StageSent extends Data.TaggedClass('StageSent')<{
  txHash: TxHash
  quote: Quote.Quote
}> {}
class StageConfirmed extends Data.TaggedClass('StageConfirmed')<{
  txHash: TxHash
  quote: Quote.Quote
}> {}
class StageFailed extends Data.TaggedClass('StageFailed')<{
  txHash: TxHash | undefined
  error: Error.UnknownError | ApiError.ApiError | undefined
  quote: Quote.Quote | undefined
}> {}

type Stage =
  | StageGettingQuote
  | StageSending
  | StageSent
  | StageConfirmed
  | StageFailed

export type SwapRequest = {
  id: string
  type: 'buy' | 'sell'
  amount: BigNumber
  settings: TradeSettings.TradeSettings
  wallet: WalletModel
  quote: Quote.Quote | undefined
  pair: PairModel
  baseCurrencyOverride?: CurrencyModel
}

export class SwapRequestModel {
  public readonly id: string
  public readonly stageAtom = atom<Stage>(new StageGettingQuote())

  constructor(public readonly request: SwapRequest) {
    this.id = request.id
  }

  public handleQuote = action((ctx, quote: Quote.Quote) => {
    this.stageAtom(ctx, new StageSending({ quote }))
  })

  public handleSendSwapData = action((ctx, data: SendSwapData) => {
    const stage = ctx.get(this.stageAtom)
    const quote = 'quote' in stage ? stage.quote : undefined
    if (!quote) {
      return
    }

    if (data.status === 'finished') {
      this.stageAtom(ctx, new StageConfirmed({ txHash: data.tx, quote }))
      return
    }
    if (data.status === 'failed') {
      this.stageAtom(
        ctx,
        new StageFailed({
          txHash: data.tx,
          error: undefined,
          quote,
        }),
      )
      return
    }
    this.stageAtom(ctx, new StageSent({ txHash: data.tx, quote }))
  })

  public handleConfirmTransactionUpdate = action(
    (ctx, data: ConfirmTransactionUpdatePayload) => {
      const stage = ctx.get(this.stageAtom)
      const quote = 'quote' in stage ? stage.quote : undefined
      if (!quote) {
        return
      }
      if (data.confirmed) {
        this.stageAtom(ctx, new StageConfirmed({ txHash: data.txHash, quote }))
        return
      }

      this.stageAtom(
        ctx,
        new StageFailed({
          txHash: data.txHash,
          error: undefined,
          quote,
        }),
      )
    },
  )

  public handleError = action(
    (ctx, error: Error.UnknownError | ApiError.ApiError) => {
      const stage = ctx.get(this.stageAtom)
      this.stageAtom(
        ctx,
        new StageFailed({
          txHash: undefined,
          error,
          quote: 'quote' in stage ? stage.quote : undefined,
        }),
      )
    },
  )

  static StageGettingQuote = StageGettingQuote
  static StageSending = StageSending
  static StageSent = StageSent
  static StageConfirmed = StageConfirmed
  static StageFailed = StageFailed
}
